import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { useEffect } from 'react';
import { MagneticSensorIcon } from '../../../Icons/Bits';
import { useAppSelector } from '../../../../redux/ReduxHooks';

const LABEL_WIDTH = 150;
const BOX_WIDTH = 120;
const BOX_HEIGHT = 40;

const valueBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  color: 'text.primary',
  width: BOX_WIDTH,
  height: BOX_HEIGHT,
  py: 0,
  px: 2,
  textAlign: 'center' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box' as const,
};

interface MagneticSwitchCheckDialogProps {
  open: boolean;
  port?: number;
  onClose: () => void;
}

const MagneticSwitchCheckDialog = ({ open, port, onClose }: MagneticSwitchCheckDialogProps) => {
  const sensorData = useAppSelector((state) => (port != null ? state.robotData.magneticSwitchData[port] ?? null : null));

  const leftSwitch = sensorData?.left_switch ?? false;
  const rightSwitch = sensorData?.right_switch ?? false;
  const magnetDetected = rightSwitch || leftSwitch;

  // Poll magnetic switch settings at a faster rate when dialog is open
  useEffect(() => {
    if (!open || port == null) return;
    const poll = () => window.robotService?.getMagneticSwitchSettings?.(port);
    poll();
    const id = setInterval(poll, 100);
    return () => clearInterval(id);
  }, [open, port]);

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
          <Box
            component={MagneticSensorIcon}
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              display: 'block',
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Box component="span" sx={{ ml: 0.5 }}>
            {port != null ? (
              <Trans i18nKey="magneticSwitchCheckDialog.titleWithPort" values={{ port }}>
                Port {{ port }}: Magnetic Sensor
              </Trans>
            ) : (
              <Trans i18nKey="magneticSwitchCheckDialog.title">Magnetic Sensor</Trans>
            )}
          </Box>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ width: 500, minHeight: 300 }}>
        <Stack spacing={2} alignItems="stretch" sx={{ width: '100%', maxWidth: 400 }}>
          <Typography>
            <Trans i18nKey="magneticSwitchCheckDialog.main-text">
              Review the live output from your magnetic sensor electronic to confirm functionality:
            </Trans>
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <Typography variant="body1" color="text.primary" sx={{ width: LABEL_WIDTH, flexShrink: 0 }}>
              <Trans i18nKey="magneticSwitchCheckDialog.magnetDetected">Magnet Detected:</Trans>
            </Typography>
            <Box
              sx={{
                ...valueBoxSx,
                bgcolor: magnetDetected ? 'success.main' : 'action.selected',
                color: magnetDetected ? 'success.contrastText' : 'text.secondary',
              }}
            >
              <Typography variant="h6" component="span" fontWeight="bold">
                {magnetDetected ? (
                  <Trans i18nKey="magneticSwitchCheckDialog.magnet-detected-yes">YES</Trans>
                ) : (
                  <Trans i18nKey="magneticSwitchCheckDialog.magnet-detected-no">NO</Trans>
                )}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" autoFocus onClick={handleClose}>
          <Trans i18nKey="magneticSwitchCheckDialog.button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default MagneticSwitchCheckDialog;
